import React, { useState, useEffect } from 'react';

// Basic Close Icon (SVG) - potentially adjust size if needed
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-5 tw-h-5"> {/* Adjusted size */}
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Basic Chevron Icons (SVG)
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-6 tw-h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-6 tw-h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const modifyProductImageUrl = (originalUrl) => {
  if (!originalUrl || typeof originalUrl !== 'string') return '';
  try {
    const url = new URL(originalUrl);
    const pathname = url.pathname;
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex === -1) return originalUrl; // No extension found in path

    const basePath = pathname.substring(0, lastDotIndex);
    const extension = pathname.substring(lastDotIndex);
    
    url.pathname = `${basePath}_400x400${extension}`;
    return url.toString();

  } catch (error) {
    console.error("Error modifying product image URL:", error);
    return originalUrl; // Fallback to original if parsing fails
  }
};

// Helper to find Greatest Common Divisor (GCD)
function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

// Helper to convert decimal to fraction string (e.g., 4.5 -> "4 1/2")
function toFraction(decimal, denominator = 16) {
  if (typeof decimal !== 'number' || isNaN(decimal)) return decimal; // Return as is if not a valid number

  const integerPart = Math.floor(decimal);
  const fractionalPart = decimal - integerPart;

  if (fractionalPart === 0) {
    return integerPart.toString();
  }

  let numerator = Math.round(fractionalPart * denominator);
  let commonDivisor = gcd(numerator, denominator);

  numerator /= commonDivisor;
  let simplifiedDenominator = denominator / commonDivisor;

  if (numerator === 0) { // Handles cases like 4.03 becoming just 4 if rounding to nearest 1/16 results in 0/16
    return integerPart.toString();
  }
  
  // If the fraction simplifies to x/1, just add it to the integer part
  if (simplifiedDenominator === 1) {
    return (integerPart + numerator).toString();
  }

  if (integerPart === 0) {
    return `${numerator}/${simplifiedDenominator}`;
  }
  return `${integerPart} ${numerator}/${simplifiedDenominator}`;
}

function TankDetailModal({ isOpen, onClose, tank, onNext, onPrevious, hasNext, hasPrevious, selectedType, onAddToCart }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen && tank) {
      setCurrentImageIndex(0);
    }
  }, [isOpen, tank]);

  if (!isOpen || !tank) return null;

  const productImageUrl = modifyProductImageUrl(tank.image);

  // Renamed: For the spec diagram at the bottom
  const getSpecDiagramUrl = (currentTank) => {
    if (!currentTank || !selectedType) return '';
    const typePrefix = selectedType === 'fresh' ? 'fresh_water_tanks' : 'holding_tanks';
    const length = Math.round(currentTank.length);
    const width = Math.round(currentTank.width);
    const height = Math.round(currentTank.height);
    return `https://d3sosgkdp1n1t7.cloudfront.net/${typePrefix}-${length}x${width}x${height}.svg`;
  };
  const specDiagramUrl = getSpecDiagramUrl(tank);

  // Array of images for the carousel
  const carouselImages = [productImageUrl, specDiagramUrl].filter(Boolean); // Filter out empty strings

  const handleContentClick = (e) => e.stopPropagation();
  const getInventoryInfo = (currentTank) => {
    if (currentTank.inventoryCount > 0) {
      return { 
        primary: `${currentTank.inventoryCount} IN STOCK`, 
        secondary: `Ships FREE in 1-2 days`, // Assuming 1-2 days for in-stock, adjust if needed
        shipsIn: "1-2 days" // Kept for consistency if used elsewhere, though secondary now has full msg
      };
    }
    let shippingEstimate = '1-2 weeks';
    const vendor = currentTank.vendor ? currentTank.vendor.toLowerCase() : '';
    if (vendor.includes('icon direct')) shippingEstimate = '2-4 days';
    else if (vendor.includes('alpha')) shippingEstimate = '1-2 weeks';
    else if (vendor.includes('elkhart plastics') || vendor.includes('ameri-kart')) shippingEstimate = '3-4 weeks';
    return { primary: "CUSTOM MADE", secondary: `Ships in ${shippingEstimate}`, shipsIn: shippingEstimate };
  };
  const inventoryInfo = getInventoryInfo(tank);

  const handleBuyNowClickInternal = (e) => {
    e.stopPropagation(); 
    if (tank && tank.variantId) { 
      onAddToCart(tank.variantId);
      onClose(); // Close the modal after adding to cart
    } else {
      console.error("TankDetailModal: Cannot add to cart. Tank data or Variant ID is missing.", tank);
      alert("There was an issue preparing this item for your cart. Please ensure product details are complete.");
    }
  };

  const handleViewDetailsClick = (e) => {
    e.stopPropagation();
    console.log("View Details clicked for tank:", tank.sku);
    if (tank && tank.handle) {
      // Assuming your Shopify store URL structure is something like:
      // https://your-store-name.myshopify.com/products/your-product-handle
      // Or if you have a custom domain:
      // https://yourcustomdomain.com/products/your-product-handle
      // Replace 'https://elkhartrvparts.com' with your actual store domain
      const productUrl = `https://elkhartrvparts.com/products/${tank.handle}`;
      window.location.href = productUrl;
    } else {
      console.error("TankDetailModal: Cannot navigate to product page. Tank data or handle is missing.", tank);
      alert("There was an issue finding the product details page.");
    }
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div 
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4"
      onClick={onClose}
    >
      {/* Main modal content container - now relative for absolute positioning of close button */}
      <div 
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-relative" 
        onClick={handleContentClick}
      >
        {/* Floating Close Button */}
        <button 
          onClick={onClose} 
          className="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-600 hover:tw-text-gray-800 tw-rounded-full tw-p-1.5 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400"
          aria-label="Close modal"
        >
          <CloseIcon />
        </button>

        {/* Modal Body (Scrollable, single column) - Added top padding to avoid overlap with new close button */}
        <div className="tw-p-6 tw-pt-3 sm:tw-pt-8 tw-overflow-y-auto tw-text-center">
            {/* 1. Image Carousel */}
            <div className="tw-w-full tw-h-56 sm:tw-h-64 tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center tw-mb-10 tw-relative">
              {carouselImages.length > 0 ? (
                <img 
                  src={carouselImages[currentImageIndex]}
                  alt={`${tank.gallons} Gallon Tank - ${currentImageIndex === 0 ? 'Product' : 'Spec Diagram'}`}
                  className="tw-max-w-full tw-max-h-full tw-object-contain p-2"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/200x200.png?text=Image+Not+Available'; }} 
                />
              ) : (
                <div className="tw-text-gray-400">Images not available</div>
              )}
              {/* Carousel Dots (only if more than one image) */}
              {carouselImages.length > 1 && (
                <div className="tw-absolute tw-bottom-[-10px] tw-left-1/2 -tw-translate-x-1/2 tw-flex tw-space-x-3">
                  {carouselImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleDotClick(idx)}
                      className={`tw-w-3 tw-h-3 tw-rounded-full ${currentImageIndex === idx ? 'tw-bg-gray-800' : 'tw-bg-gray-400 hover:tw-bg-gray-600'} focus:tw-outline-none`}
                      aria-label={`Go to image ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 2. Dynamic Title */}
            <h3 className="tw-text-xl tw-font-semibold tw-text-gray-800 tw-leading-tight">
                {tank.gallons} Gallon {selectedType === 'fresh' ? 'Fresh Water' : 'Holding'} Tank
            </h3>
            <p className="tw-text-md tw-text-gray-700 tw-mb-2">
                {toFraction(tank.length)}" x {toFraction(tank.width)}" x {toFraction(tank.height)}"
            </p>

            {/* 4. Inventory Info */}
            <div className="tw-mb-4">
                <p className={`tw-text-lg tw-font-semibold ${inventoryInfo.primary.includes("IN STOCK") ? "tw-text-green-600" : "tw-text-amber-700"}`}>{inventoryInfo.primary}</p>
                <p className="tw-text-sm tw-text-gray-600">
                    {inventoryInfo.secondary}
                </p>
            </div>

            {/* 3. Price */}
            <p className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-2">
                ${tank.price ? tank.price.toFixed(2) : 'N/A'} + FREE Shipping
            </p>

            {/* 5. BUY NOW Button */}
            <button 
                onClick={handleBuyNowClickInternal}
                className="tw-w-full tw-px-6 tw-py-3 tw-text-xl tw-bg-brand-red hover:tw-bg-brand-red-dark tw-text-white tw-font-semibold tw-rounded-md tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-red focus:tw-ring-opacity-50 transition-colors duration-200 tw-mb-2"
            >
                BUY NOW
            </button>

            {/* 6. View Details Button */}
            <button 
                onClick={handleViewDetailsClick}
                className="tw-w-full tw-px-6 tw-py-2 tw-bg-white-100 tw-underline hover:tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-opacity-50 transition-colors duration-200 tw-mb-4"
            >
                View Full Details
            </button>
        </div>

        {/* Modal Footer (Navigation) */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-t tw-border-gray-200">
          <button 
            onClick={onPrevious} 
            disabled={!hasPrevious} 
            className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded hover:tw-bg-gray-300 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center"
          >
            <ChevronLeftIcon /> <span className="tw-ml-2">Previous</span>
          </button>
          <button 
            onClick={onNext} 
            disabled={!hasNext} 
            className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded hover:tw-bg-gray-300 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center"
          >
            <span className="tw-mr-2">Next</span> <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

export default TankDetailModal; 